import { X } from 'lucide-react';
import type React from 'react';
import { type CSSProperties, useEffect, useId } from 'react';
import { useResizableWidth } from '../../hooks/useResizableWidth.js';
import {
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetTitle,
} from '../tailgrids/core/sheet.js';
import { cn } from '../../utils/cn.js';

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleIcon?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** Panel width from `sm` up; on phones the panel is always full width. */
  width?: 'sm' | 'md' | 'lg' | 'xl';
  side?: 'right' | 'left';
  /**
   * `false` keeps the page behind usable (no backdrop, no focus trap) — a side panel that works beside the page, like
   * an entity assistant. Escape still closes it.
   */
  modal?: boolean;
  /**
   * Non-modal only: the person drags the inner edge to resize the panel; the width is remembered under `storageKey`.
   */
  resizable?: { storageKey: string; defaultWidth?: number; minWidth?: number; maxWidth?: number };
}

const RESIZE_DEFAULT = { defaultWidth: 520, minWidth: 360, maxWidth: 1200 };
/** How much of the window the panel may take, so the page stays reachable. */
const PAGE_GUTTER_PX = 48;

const WIDTH = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
};

const CLOSE_CLASS =
  'flex size-7 shrink-0 items-center justify-center rounded-md text-text-100 hover:bg-background-gray-secondary hover:text-title-50 data-focus-visible:ring-2 data-focus-visible:ring-primary-500 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none';

/** The non-modal variant: the same panel, fixed to one side, without a backdrop or a focus trap. */
function SidePanel({
  open,
  onClose,
  title,
  titleIcon,
  description,
  children,
  footer,
  width = 'md',
  side = 'right',
  resizable,
}: SheetProps) {
  const titleId = useId();
  const limits = { ...RESIZE_DEFAULT, ...resizable };
  const resize = useResizableWidth({
    defaultWidth: limits.defaultWidth,
    minWidth: limits.minWidth,
    maxWidth:
      typeof window === 'undefined'
        ? limits.maxWidth
        : Math.min(limits.maxWidth, Math.max(limits.minWidth, window.innerWidth - PAGE_GUTTER_PX)),
    resizeFrom: side === 'right' ? 'left' : 'right',
    storageKey: resizable?.storageKey,
  });
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      style={resizable ? ({ '--panel-width': `${resize.width}px` } as CSSProperties) : undefined}
      className={cn(
        'fixed inset-y-0 z-50 flex h-full w-full min-w-xs flex-col bg-card-background shadow-lg',
        side === 'right' ? 'right-0 border-l border-base-100' : 'left-0 border-r border-base-100',
        resizable ? 'sm:w-(--panel-width) sm:max-w-none' : WIDTH[width],
      )}
    >
      {resizable && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          onMouseDown={resize.onResizeStart}
          className={cn(
            'absolute inset-y-0 hidden w-1.5 cursor-col-resize hover:bg-primary-500/30 sm:block',
            side === 'right' ? '-left-0.5' : '-right-0.5',
          )}
        />
      )}
      <header className="flex shrink-0 items-start justify-between gap-3 border-b border-card-border py-4 pr-3 pl-5">
        <div className="min-w-0">
          <h2 id={titleId} className="flex items-center gap-2 text-base leading-6 font-semibold text-title-50">
            {titleIcon}
            {title}
          </h2>
          {description && <p className="mt-1 text-sm text-text-100">{description}</p>}
        </div>
        <button type="button" aria-label="Close" onClick={onClose} className={CLOSE_CLASS}>
          <X size={18} />
        </button>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <footer className="shrink-0 border-t border-card-border px-5 py-4">{footer}</footer>}
    </section>
  );
}

/**
 * A side panel over the page (NextAdmin sheet on react-aria): help, a detail preview, an assistant. Focus is trapped
 * and restored, Escape and the backdrop close it; `modal={false}` keeps the page usable beside it. Never build a
 * drawer with `fixed inset-0` by hand.
 */
export function Sheet(props: SheetProps) {
  const {
    open,
    onClose,
    title,
    titleIcon,
    description,
    children,
    footer,
    width = 'md',
    side = 'right',
    modal = true,
  } = props;
  if (!modal) return <SidePanel {...props} />;
  return (
    <SheetOverlay isOpen={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      {/* `bg-card-background`: the vendored sheet paints TailGrids' `background-100` (#111827 in dark), which is not
          a NextAdmin surface; panels use the card surface like every other NextAdmin panel. */}
      <SheetContent side={side} showCloseButton={false} className={cn('gap-0 bg-card-background p-0', WIDTH[width])}>
        <SheetHeader className="shrink-0 flex-row items-start justify-between gap-3 border-b border-card-border py-4 pr-3 pl-5">
          <div className="min-w-0">
            <SheetTitle className="flex items-center gap-2 text-base leading-6">
              {titleIcon}
              {title}
            </SheetTitle>
            {description && <SheetDescription className="mt-1">{description}</SheetDescription>}
          </div>
          <SheetClose aria-label="Close" className={CLOSE_CLASS}>
            <X size={18} />
          </SheetClose>
        </SheetHeader>
        <SheetBody className="mx-0 px-5 py-4">{children}</SheetBody>
        {footer && <SheetFooter className="shrink-0 border-t border-card-border px-5 py-4">{footer}</SheetFooter>}
      </SheetContent>
    </SheetOverlay>
  );
}
